import React, { useState } from 'react';
import { EVENT_TYPES } from '../../constants/eventTypes';

// ─── Guest Event Form (for hosts) ────────────────────────────────────────────
// Simplified form for posting events that appear on the Upcoming Events page.
// No vendor categories or booth logistics — just event details for guests.
// Host-submitted guest events require admin approval before going live.
function HostGuestEventForm({ onSubmit, authUser, setShowAuthModal, openSignup }) {
  const [form, setForm] = useState({
    orgName:'', eventName:'', eventTypes:[], date:'', startTime:'', endTime:'',
    zip:'', address:'', isTicketed:false, ticketPrice:'', eventLink:'', eventGoerNotes:'',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleSubmit = async () => {
    if (!authUser) { if (setShowAuthModal) setShowAuthModal(true); return; }
    if (!form.eventName.trim()) { setError('Please enter an event name.'); return; }
    if (!(form.eventTypes||[]).length) { setError('Please select at least one event type.'); return; }
    if (!form.zip || !/^\d{5}$/.test(form.zip)) { setError('Please enter a valid 5-digit zip code.'); return; }
    if (!form.date) { setError('Please select a date.'); return; }
    setError(''); setSubmitting(true);

    try {
      await onSubmit({
        eventName: form.eventName.trim(),
        eventType: (form.eventTypes||[])[0] || '',
        eventTypes: form.eventTypes || [],
        date: form.date,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        eventZip: form.zip,
        orgName: form.orgName.trim() || null,
        contactName: authUser.user_metadata?.full_name || authUser.email,
        email: authUser.email,
        isTicketed: form.isTicketed,
        ticketPrice: form.isTicketed ? form.ticketPrice : null,
        eventLink: form.eventLink || null,
        eventGoerNotes: form.eventGoerNotes || null,
        address: form.address || null,
        vendorCategories: [],
        servicesNeeded: [],
        shareWithEventGoers: true,
        vendorDiscovery: 'none',
        isGuestEvent: true,
      });
      setSuccess(true);
    } catch (err) {
      console.error('Guest event submit error:', err);
      setError('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  if (!authUser) {
    return (
      <div className="section" style={{maxWidth:600,textAlign:'center',padding:'60px 20px'}}>
        <div style={{fontSize:28,marginBottom:12}}>🎪</div>
        <h2 style={{fontFamily:'Playfair Display,serif',fontSize:24,color:'#1a1410',marginBottom:8}}>Share a Local Event</h2>
        <p style={{color:'#7a6a5a',fontSize:14,marginBottom:20}}>Log in or create an account to post an event for local guests to discover.</p>
        <button onClick={()=>openSignup ? openSignup('host') : setShowAuthModal(true)} className="btn-submit" style={{fontSize:15,padding:'12px 36px'}}>Log In / Sign Up</button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="section" style={{maxWidth:600,textAlign:'center',padding:'60px 20px'}}>
        <div style={{fontSize:48,marginBottom:12}}>&#10003;</div>
        <h2 style={{fontFamily:'Playfair Display,serif',fontSize:24,color:'#1a6b3a',marginBottom:8}}>Event Submitted!</h2>
        <p style={{color:'#7a6a5a',fontSize:14,marginBottom:8}}>Your event has been submitted for review. Once approved, it will appear on the Upcoming Events page for local event guests to discover.</p>
        <p style={{color:'#a89a8a',fontSize:13}}>You'll receive an email when your event is approved.</p>
      </div>
    );
  }

  return (
    <div className="section" style={{maxWidth:640}}>
      <div className="section-title">Share a Local Event</div>
      <p className="section-sub" style={{marginBottom:24}}>This event will appear on the <strong>Upcoming Events</strong> page for local event guests to discover. No vendor setup needed — just the event details.</p>

      <div style={{background:'#fff',border:'1px solid #e8ddd0',borderRadius:12,padding:24}}>
        <div className="form-grid" style={{gap:12,marginBottom:16}}>
          <div className="form-group full"><label>Event Name <span style={{color:'#c0392b'}}>*</span></label><input value={form.eventName} onChange={e=>set('eventName',e.target.value)} placeholder="e.g. Cherry Hill Spring Market" /></div>
          <div className="form-group"><label>Organization / Venue</label><input value={form.orgName} onChange={e=>set('orgName',e.target.value)} placeholder="e.g. White Horse Winery" /></div>
          <div className="form-group full"><label>Event Type <span style={{color:'#c0392b'}}>*</span> <span style={{fontSize:11,color:'#a89a8a',fontWeight:400}}>(select all that apply)</span></label>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {EVENT_TYPES.filter(t=>t!=='Other'&&t!=='Private Party').map(t=>(
                <button key={t} type="button" onClick={()=>set('eventTypes', (form.eventTypes||[]).includes(t) ? (form.eventTypes||[]).filter(x=>x!==t) : [...(form.eventTypes||[]),t])}
                  style={{padding:'5px 12px',borderRadius:16,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:'1.5px solid',
                    background:(form.eventTypes||[]).includes(t)?'#c8a850':'#fff',
                    color:(form.eventTypes||[]).includes(t)?'#1a1410':'#7a6a5a',
                    borderColor:(form.eventTypes||[]).includes(t)?'#c8a850':'#e8ddd0'}}>{t}</button>
              ))}
            </div>
          </div>
          <div className="form-group"><label>Date <span style={{color:'#c0392b'}}>*</span></label><input type="date" value={form.date} onChange={e=>set('date',e.target.value)} min={new Date().toISOString().split('T')[0]} /></div>
          <div className="form-group"><label>Zip Code <span style={{color:'#c0392b'}}>*</span></label><input value={form.zip} onChange={e=>set('zip',e.target.value.replace(/\D/g,'').slice(0,5))} placeholder="08033" maxLength={5} /></div>
          <div className="form-group"><label>Start Time</label><input type="time" value={form.startTime} onChange={e=>set('startTime',e.target.value)} /></div>
          <div className="form-group"><label>End Time</label><input type="time" value={form.endTime} onChange={e=>set('endTime',e.target.value)} /></div>
          <div className="form-group full"><label>Venue Address</label><input value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Street address (optional)" /></div>
          <div className="form-group"><label>Ticketed?</label>
            <select value={form.isTicketed?'yes':'no'} onChange={e=>set('isTicketed',e.target.value==='yes')}>
              <option value="no">Free admission</option><option value="yes">Ticketed</option>
            </select>
          </div>
          {form.isTicketed && <div className="form-group"><label>Ticket Price</label><input value={form.ticketPrice} onChange={e=>set('ticketPrice',e.target.value)} placeholder="e.g. $10 adults / kids free" /></div>}
          <div className="form-group full"><label>Event Link</label><input value={form.eventLink} onChange={e=>set('eventLink',e.target.value)} placeholder="Facebook event, website, or ticket link" /></div>
          <div className="form-group full"><label>Details for Guests</label>
            <textarea value={form.eventGoerNotes} onChange={e=>set('eventGoerNotes',e.target.value)} placeholder="Parking info, what to expect, food options, rain-or-shine policy..." rows={3} style={{width:'100%',borderRadius:8,border:'1px solid #e0d5c5',padding:'8px 12px',fontSize:14,fontFamily:'DM Sans,sans-serif',resize:'vertical',boxSizing:'border-box'}} />
          </div>
        </div>

        {error && <div style={{color:'#c0392b',fontSize:13,marginBottom:12,background:'#fdecea',border:'1px solid #f5c6c6',borderRadius:8,padding:'8px 12px'}}>{error}</div>}

        <div style={{background:'#fdf9f5',border:'1px solid #e8ddd0',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#7a5a10'}}>
          Your event will be reviewed before going live. You'll receive an email once it's approved.
        </div>

        <button onClick={handleSubmit} disabled={submitting} className="btn-submit" style={{width:'100%',fontSize:15,padding:'12px',opacity:submitting?0.6:1}}>
          {submitting ? 'Submitting...' : 'Submit Event for Review'}
        </button>
      </div>
    </div>
  );
}

export default HostGuestEventForm;
